// Number / date / HTML formatting helpers.
// Ported verbatim (behaviour-preserving) from the original www/app.js, now typed.
// Locale-aware since the i18n pass: numbers/dates follow the UI locale (Swiss
// conventions per language); relTime phrases come from the chrome catalog.
import { monthsShort, numberLocale, tn, t } from "./locale";

/** Locale-formatted number with fixed decimals; null/undefined -> "". */
export const fmtNum = (v: number | null | undefined, d = 2): string =>
  v == null
    ? ""
    : Number(v).toLocaleString(numberLocale(), {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      });

/** Locale-formatted number with at least `d` decimals, and more when |v| < 1 so
 *  that it keeps three significant digits: 0.00054 stays "0.00054" instead of
 *  "0.00". Values from 1 up format exactly as fmtNum(v, d). */
export function fmtDec(v: number, d = 2): string {
  const a = Math.abs(v);
  if (a === 0 || a >= 1) return fmtNum(v, d);
  const sig = 2 - Math.floor(Math.log10(a));   // decimals for 3 significant digits
  return Number(v).toLocaleString(numberLocale(), {
    minimumFractionDigits: d,
    maximumFractionDigits: Math.min(Math.max(d, sig), 10),
  });
}

/** "2026-05-22 13:15:47" or "2026-05-22" -> "22 May 2026"; empty -> "".
 *  A calendar date, not an instant: `new Date("2026-01-01")` is midnight UTC, and
 *  formatting it in the viewer's zone showed 31 Dec for anyone west of UTC. So
 *  the date is read and written in UTC. */
export function fmtDate(s: string | null | undefined): string {
  if (!s) return "";
  const iso = String(s).slice(0, 10);
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(numberLocale(), { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

/** The period an observation stands for, from its period-start date: "Q2 2026",
 *  "Sep 2026", "2025", else the day. */
export function periodLabel(iso: string, freq: string | null | undefined): string {
  const d = new Date(iso), y = d.getUTCFullYear(), mo = d.getUTCMonth();
  if (isNaN(y)) return iso;
  if (freq === "quarterly") return `Q${Math.floor(mo / 3) + 1} ${y}`;
  if (freq === "semi-annual") return `H${mo < 6 ? 1 : 2} ${y}`;
  if (freq === "monthly") return `${monthsShort()[mo]} ${y}`;
  if (freq === "annual") return String(y);
  return fmtDate(iso);
}

/** A headline value with the decimals its size needs: 12,345 / 123.4 / 1.23,
 *  and 3 significant digits below 1 (0.00054). */
export const fmtStat = (v: number): string =>
  fmtDec(v, Math.abs(v) >= 1000 ? 0 : Math.abs(v) >= 100 ? 1 : 2);

/** Compact y-axis tick: 9_000_000 -> "9M", 134_000 -> "134k", 2.5 -> "2.5". */
export function fmtTick(v: number | null | undefined): string {
  if (v == null) return "";
  const a = Math.abs(v);
  const trim = (x: number): string => (Math.round(x * 10) / 10).toString();
  if (a >= 1e9) return trim(v / 1e9) + "B";
  if (a >= 1e6) return trim(v / 1e6) + "M";
  if (a >= 1e3) return trim(v / 1e3) + "k";
  if (a < 1) return Number(v.toPrecision(3)).toString();   // 0.00054, not 0
  return (Math.round(v * 100) / 100).toString();
}

/** HTML-escape a value for safe interpolation into markup. */
const ESC: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" };
export const esc = (s: unknown): string =>
  String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ESC[c] ?? c);

/** Parse an API timestamp as UTC. The catalog's `fetched` is space-separated
 * ("2026-06-06 08:06:49"), which browsers parse as *local* time — that would skew
 * a relative label by the viewer's offset. So normalise to explicit-UTC ISO first;
 * also accepts an already-zoned ISO string or a bare date. null if unparseable. */
export function parseStampUTC(s: string | null | undefined): Date | null {
  if (!s) return null;
  let t = String(s).trim().replace(" ", "T");
  if (!/([zZ]|[+-]\d\d:?\d\d)$/.test(t)) t += "Z"; // no zone given -> assume UTC
  const d = new Date(t);
  return isNaN(d.getTime()) ? null : d;
}

/** Coarse "time ago" label from a past Date: "just now", "30 minutes ago",
 * "3 hours ago", "1 day ago", "4 days ago". Conveys freshness without the reader
 * needing to know today's date. `now` is injectable for deterministic tests. */
export function relTime(d: Date, now: Date = new Date()): string {
  const sec = Math.max(0, Math.floor((now.getTime() - d.getTime()) / 1000));
  if (sec < 90) return t("just now");
  const one = (n: number, s: string, p: string) => (n === 1 ? tn(s, n) : tn(p, n));
  const min = Math.floor(sec / 60);
  if (min < 60) return one(min, "{n} minute ago", "{n} minutes ago");
  const hr = Math.floor(sec / 3600);
  if (hr < 24) return one(hr, "{n} hour ago", "{n} hours ago");
  const day = Math.floor(sec / 86400);
  return one(day, "{n} day ago", "{n} days ago");
}
